import { Button, Table, Typography } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import PermissionCheck from "~Common/PermissionCheck";
import AddPersonModal from "~components/AddPersonModal";
import styles from "~components/AddPersonRolesModal/AddPersonRolesModal.module.css";
import { ImageTypes } from "~enums/imageTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { PersonStatuses } from "~enums/personStatuses";
import { useFetchPersons } from "~hooks/apiHooks/useFetchPersons";
import { useHandlePagination } from "~hooks/useHandlePagination";
import AddPersonToRosterFilters from "~pages/Roster/components/AddPlayerToRoster/components/AddPersonToRosterFilters";
import { useGetAddPersonToRosterColumns } from "~pages/Roster/components/AddPlayerToRoster/hooks/useGetAddPersonToRosterColumns";
import type { AddPlayerToRosterTableRow } from "~pages/Roster/components/AddPlayerToRoster/types/AddPlayerToRosterTableRow";
import type { SeasonRosterDataTableRow } from "~pages/Roster/SeasonRosterListPage/types/seasonRosterDataTableRow.ts";
import type { PersonResponse } from "~types/person";
import type { RosterShort } from "~types/rosterShort";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";
import { onTableChange } from "~utilities/onTableChange";

const DEFAULT_SORT_FIELD = "nameFullLocal";

const transformAddPersonToRosterResponse = (data: PersonResponse): Array<AddPlayerToRosterTableRow> =>
  data.data.map((person) => ({
    nameFullLocal: person.nameFullLocal,
    avatarUrl: findImageByTypes(person?.images ?? [], [ImageTypes.PersonHead])?.url ?? "",
    nationality: person.nationality,
    gender: person.gender,
    personId: person.personId,
    dob: person.dob,
    status: person.status,
  }));

type Props = {
  handleAdd: (value: unknown) => void;
  tableData: Array<SeasonRosterDataTableRow>;
  seasonRosterShort?: RosterShort[];
};

const AddPlayerToSeasonRoster = ({ handleAdd, tableData, seasonRosterShort }: Props) => {
  const [isAddPersonModalOpen, setIsAddPersonModalOpen] = useState(false);
  const { formatMessage } = useIntl();
  const [searchValue, setSearchValue] = useState("");
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions, resetPagination } = useHandlePagination();

  const [nationality, setNationality] = useState<string | undefined>();
  const [gender, setGender] = useState<string | undefined>();
  const [status, setStatus] = useState<PersonStatuses | undefined>();
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_FIELD);

  const params: Record<string, string> = {
    limit: pageSize.toString(),
    sortBy,
    offset: offset.toString(),
    ...(searchValue.length > 1 && { nameFullLocalContains: searchValue }),
    ...(nationality && { nationality }),
    ...(gender && { gender }),
    ...(status && { status }),
  };

  const {
    data: dataSource,
    isFetching,
    refetch: refetchPersons,
  } = useFetchPersons<AddPlayerToRosterTableRow[]>(params, (data) => transformAddPersonToRosterResponse(data));

  const isLoading = isFetching;

  const handleAddPerson = (tableRow: AddPlayerToRosterTableRow) => {
    handleAdd({ personId: tableRow.personId });
  };

  const columns = useGetAddPersonToRosterColumns(isLoading, handleAddPerson, true, tableData || [], undefined, seasonRosterShort);

  const onChange = onTableChange<AddPlayerToRosterTableRow>(setSortBy, DEFAULT_SORT_FIELD);

  const onCreateCb = async (localFullName: string) => {
    await refetchPersons();
    resetPagination();
    setSearchValue(localFullName);
  };

  return (
    <>
      <div className={styles.topBar}>
        <div className={styles.topLeftContainer}>
          <Typography.Title level={4} className={styles.title}>
            {formatMessage({ id: "persons" })}
          </Typography.Title>
        </div>
        <div className={styles.topRightContainer}>
          <AddPersonToRosterFilters
            setGender={setGender}
            setNationality={setNationality}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setStatus={setStatus}
            disabled={isLoading}
            resetPagination={resetPagination}
          />
          <PermissionCheck permissionKeys={[PermissionNames.PERSON]} permissionOptions={[PermissionOperations.CREATE]}>
            <Button onClick={() => setIsAddPersonModalOpen(true)} type="primary" icon={<i className="fas fa-plus" />} disabled={isLoading}>
              {formatMessage({ id: "person" })}
            </Button>
            <AddPersonModal open={isAddPersonModalOpen} handleClose={() => setIsAddPersonModalOpen(false)} onAdd={onCreateCb} />
          </PermissionCheck>
        </div>
      </div>
      <Table
        size="small"
        dataSource={dataSource}
        rowKey="personId"
        loading={isLoading}
        columns={columns}
        className={styles.tableContainer}
        onChange={onChange}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: offset + (dataSource?.length ?? 0) + 1,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: () => formatMessage({ id: "pageN" }, { number: currentPage }),
        }}
      />
    </>
  );
};

export default AddPlayerToSeasonRoster;
